import {
  sendToGoogleAnalytics,
  sendToGoogleAnalyticsClient,
  sendGoogleAdsConversion,
} from './google'
import { sendToSocialPixel } from './social'
import { sendToRedditConversions } from './reddit'
import { getServerTrackingData, getClientTrackingData } from './utils'
import { CONVERSION_EVENT_TYPES } from './types'
import type { ConversionEventData, ConversionEventType } from './types'

// Re-export types for external use
export * from './types'

/**
 * 统一的事件发送函数
 * 同时发送到所有配置的平台
 */
export async function sendConversionEvent(
  eventData: ConversionEventData,
): Promise<void> {
  const skipServerGA = eventData.skip_server_ga === true

  const promises = [
    skipServerGA ? Promise.resolve() : sendToGoogleAnalytics(eventData),
    sendToSocialPixel(eventData),
    sendToRedditConversions(eventData),
  ]

  // 并行发送到所有平台，不等待任何一个失败
  await Promise.allSettled(promises)
}

/**
 * 客户端直接发送到GA/Google Ads，确保使用真实的client/session信号
 */
export async function sendConversionEventClient(
  eventData: ConversionEventData,
): Promise<void> {
  // Send GA4 event with client-side gtag
  sendToGoogleAnalyticsClient(eventData)

  // Determine if this event should trigger a Google Ads conversion
  const shouldTrackGoogleAdsConversion =
    eventData.event_type === CONVERSION_EVENT_TYPES.SIGNUP ||
    eventData.event_type === CONVERSION_EVENT_TYPES.REFERRAL_SIGNUP ||
    eventData.event_type === CONVERSION_EVENT_TYPES.CHECKOUT ||
    eventData.event_type === CONVERSION_EVENT_TYPES.ORDER_SUCCESS ||
    eventData.event_type === CONVERSION_EVENT_TYPES.SUBSCRIBE_SUCCESS

  if (shouldTrackGoogleAdsConversion) {
    // For Sign-up events, generate a unique transaction ID if not provided
    const transactionId =
      eventData.transaction_id ||
      `${eventData.event_type}_${eventData.user_id}_${Date.now()}`

    // For Sign-up and Checkout events, value might be 0 or undefined
    const value = eventData.value || 0

    await sendGoogleAdsConversion(
      transactionId,
      value,
      eventData.currency || 'USD',
      eventData.user_email,
      undefined, // Let the function auto-select the conversion label
      eventData.event_type, // Pass event type for auto label selection
    )
  }
}

/**
 * 便捷的事件发送函数 - 优化用于服务端转化追踪
 */
export async function trackSignup(userData: {
  user_id: string
  user_email: string
  source?: string
  ga_client_id?: string
  ga_session_id?: number
  ga_session_number?: number
  gclid?: string
  wbraid?: string
  gbraid?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  page_url?: string
  referrer_url?: string
  request?: Request // 服务端请求对象
}): Promise<void> {
  const serverData = userData.request
    ? getServerTrackingData(userData.request)
    : {}

  await sendConversionEvent({
    event_type: CONVERSION_EVENT_TYPES.SIGNUP,
    user_id: userData.user_id,
    user_email: userData.user_email,
    signup_source: userData.source || 'website',
    ga_client_id: userData.ga_client_id,
    ga_session_id: userData.ga_session_id,
    ga_session_number: userData.ga_session_number,
    gclid: userData.gclid,
    wbraid: userData.wbraid,
    gbraid: userData.gbraid,
    utm_source: userData.utm_source,
    utm_medium: userData.utm_medium,
    utm_campaign: userData.utm_campaign,
    utm_term: userData.utm_term,
    utm_content: userData.utm_content,
    page_url: userData.page_url,
    referrer_url: userData.referrer_url,
    ...serverData,
  })
}

export async function trackReferralSignup(userData: {
  user_id: string
  user_email: string
  referral_code?: string
  request?: Request
}): Promise<void> {
  const serverData = userData.request
    ? getServerTrackingData(userData.request)
    : {}

  await sendConversionEvent({
    event_type: CONVERSION_EVENT_TYPES.REFERRAL_SIGNUP,
    user_id: userData.user_id,
    user_email: userData.user_email,
    referral_code: userData.referral_code,
    ...serverData,
  })
}

export async function trackLogin(userData: {
  user_id: string
  user_email: string
  ga_client_id?: string
  ga_session_id?: number
  ga_session_number?: number
  gclid?: string
  wbraid?: string
  gbraid?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  page_url?: string
  referrer_url?: string
  request?: Request
}): Promise<void> {
  const serverData = userData.request
    ? getServerTrackingData(userData.request)
    : {}

  await sendConversionEvent({
    event_type: CONVERSION_EVENT_TYPES.LOGIN,
    user_id: userData.user_id,
    user_email: userData.user_email,
    ga_client_id: userData.ga_client_id,
    ga_session_id: userData.ga_session_id,
    ga_session_number: userData.ga_session_number,
    gclid: userData.gclid,
    wbraid: userData.wbraid,
    gbraid: userData.gbraid,
    utm_source: userData.utm_source,
    utm_medium: userData.utm_medium,
    utm_campaign: userData.utm_campaign,
    utm_term: userData.utm_term,
    utm_content: userData.utm_content,
    page_url: userData.page_url,
    referrer_url: userData.referrer_url,
    ...serverData,
  })
}

export async function trackStartCoding(codingData: {
  user_id: string
  user_email: string
  project_id?: string
  request?: Request
}): Promise<void> {
  const serverData = codingData.request
    ? getServerTrackingData(codingData.request)
    : {}

  await sendConversionEvent({
    event_type: CONVERSION_EVENT_TYPES.START_CODING,
    user_id: codingData.user_id,
    user_email: codingData.user_email,
    project_id: codingData.project_id,
    ...serverData,
  })
}

export async function trackFinishCoding(codingData: {
  user_id: string
  user_email: string
  project_id?: string
  request?: Request
}): Promise<void> {
  const serverData = codingData.request
    ? getServerTrackingData(codingData.request)
    : {}

  await sendConversionEvent({
    event_type: CONVERSION_EVENT_TYPES.FINISH_CODING,
    user_id: codingData.user_id,
    user_email: codingData.user_email,
    project_id: codingData.project_id,
    ...serverData,
  })
}

export async function trackCheckout(checkoutData: {
  user_id: string
  user_email: string
  value: number
  currency: string
  request?: Request
}): Promise<void> {
  const serverData = checkoutData.request
    ? getServerTrackingData(checkoutData.request)
    : {}

  await sendConversionEvent({
    event_type: CONVERSION_EVENT_TYPES.CHECKOUT,
    user_id: checkoutData.user_id,
    user_email: checkoutData.user_email,
    value: checkoutData.value,
    currency: checkoutData.currency,
    ...serverData,
  })
}

export async function trackOrderSuccess(orderData: {
  user_id: string
  user_email: string
  transaction_id: string
  value: number
  currency: string
  items?: Array<{
    item_id: string
    item_name: string
    price: number
    quantity: number
  }>
  request?: Request
}): Promise<void> {
  const serverData = orderData.request
    ? getServerTrackingData(orderData.request)
    : {}

  await sendConversionEvent({
    event_type: CONVERSION_EVENT_TYPES.ORDER_SUCCESS,
    user_id: orderData.user_id,
    user_email: orderData.user_email,
    transaction_id: orderData.transaction_id,
    value: orderData.value,
    currency: orderData.currency,
    items: orderData.items,
    conversion_id: orderData.transaction_id, // 使用订单ID作为转化ID
    ...serverData,
  })
}

export async function trackSubscribeSuccess(subscriptionData: {
  user_id: string
  user_email: string
  plan_name: string
  plan_price: number
  currency: string
  transaction_id: string
  request?: Request
}): Promise<void> {
  const serverData = subscriptionData.request
    ? getServerTrackingData(subscriptionData.request)
    : {}

  await sendConversionEvent({
    event_type: CONVERSION_EVENT_TYPES.SUBSCRIBE_SUCCESS,
    user_id: subscriptionData.user_id,
    user_email: subscriptionData.user_email,
    value: subscriptionData.plan_price,
    currency: subscriptionData.currency,
    transaction_id: subscriptionData.transaction_id,
    plan_name: subscriptionData.plan_name,
    conversion_id: subscriptionData.transaction_id, // 使用订阅订单ID作为转化ID
    ...serverData,
  })
}

/**
 * Track subscription purchase with Google Ads conversion
 * This function sends both GA4 purchase event and Google Ads conversion
 * Should be called from client-side after payment success
 */
export async function trackSubscriptionPurchase(
  transactionId: string,
  amount: number,
  userEmail?: string,
  currency: string = 'USD',
): Promise<void> {
  // Import Google Ads conversion function
  const { sendGoogleAdsConversion } = await import('./google')

  // Send Google Ads conversion (client-side only)
  await sendGoogleAdsConversion(transactionId, amount, currency, userEmail)

  // Note: GA4 purchase event should already be sent server-side via sendConversionEvent
  // This is just for Google Ads client-side conversion tracking
}

/**
 * Track subscription renewal event
 * IMPORTANT: This sends a custom event (subscription_renewal), NOT a purchase event
 * Renewals should NOT be counted as new purchases to avoid inflating ROAS metrics
 * This allows separate tracking of recurring revenue for annual revenue analysis
 *
 * @param renewalData - Renewal event data
 * @param renewalData.user_id - User ID
 * @param renewalData.user_email - User email
 * @param renewalData.subscription_id - Subscription ID
 * @param renewalData.invoice_id - Invoice ID (for deduplication)
 * @param renewalData.amount - Renewal amount
 * @param renewalData.currency - Currency code (default: 'USD')
 * @param renewalData.plan - Subscription plan name
 * @param renewalData.billing_interval - Billing interval ('monthly' or 'yearly')
 * @param renewalData.request - Optional server request object for tracking params
 */
export async function trackSubscriptionRenewal(renewalData: {
  user_id: string
  user_email: string
  subscription_id: string
  invoice_id: string
  amount: number
  currency?: string
  plan?: string
  plan_name?: string
  billing_interval?: string
  ga_client_id?: string
  ga_session_id?: number
  ga_session_number?: number
  gclid?: string
  wbraid?: string
  gbraid?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  page_url?: string
  referrer_url?: string
  request?: Request
}): Promise<void> {
  const serverData = renewalData.request
    ? getServerTrackingData(renewalData.request)
    : {}

  await sendConversionEvent({
    event_type: CONVERSION_EVENT_TYPES.SUBSCRIPTION_RENEWAL,
    user_id: renewalData.user_id,
    user_email: renewalData.user_email,
    value: renewalData.amount,
    currency: renewalData.currency || 'USD',
    transaction_id: renewalData.invoice_id,
    subscription_id: renewalData.subscription_id,
    plan: renewalData.plan,
    plan_name: renewalData.plan_name,
    billing_interval: renewalData.billing_interval,
    conversion_id: `renewal_${renewalData.invoice_id}`, // Unique ID for renewal event
    ga_client_id: renewalData.ga_client_id,
    ga_session_id: renewalData.ga_session_id,
    ga_session_number: renewalData.ga_session_number,
    gclid: renewalData.gclid,
    wbraid: renewalData.wbraid,
    gbraid: renewalData.gbraid,
    utm_source: renewalData.utm_source,
    utm_medium: renewalData.utm_medium,
    utm_campaign: renewalData.utm_campaign,
    utm_term: renewalData.utm_term,
    utm_content: renewalData.utm_content,
    page_url: renewalData.page_url,
    referrer_url: renewalData.referrer_url,
    ...serverData,
  })
}

/**
 * Track subscription cancellation event
 * This helps analyze churn rate, retention metrics, and identify cancellation patterns
 *
 * @param cancellationData - Cancellation event data
 * @param cancellationData.user_id - User ID
 * @param cancellationData.user_email - User email
 * @param cancellationData.subscription_id - Subscription ID
 * @param cancellationData.plan - Subscription plan name
 * @param cancellationData.billing_interval - Billing interval ('monthly' or 'yearly')
 * @param cancellationData.cancellation_reason - Optional cancellation reason
 * @param cancellationData.cancel_at_period_end - Whether cancellation is at period end
 * @param cancellationData.request - Optional server request object for tracking params
 */
export async function trackSubscriptionCancellation(cancellationData: {
  user_id: string
  user_email: string
  subscription_id: string
  plan?: string
  plan_name?: string
  billing_interval?: string
  cancellation_reason?: string | null
  cancel_at_period_end?: boolean
  request?: Request
}): Promise<void> {
  const serverData = cancellationData.request
    ? getServerTrackingData(cancellationData.request)
    : {}

  await sendConversionEvent({
    event_type: CONVERSION_EVENT_TYPES.SUBSCRIPTION_CANCELLATION,
    user_id: cancellationData.user_id,
    user_email: cancellationData.user_email,
    subscription_id: cancellationData.subscription_id,
    plan: cancellationData.plan,
    plan_name: cancellationData.plan_name,
    billing_interval: cancellationData.billing_interval,
    cancellation_reason: cancellationData.cancellation_reason || null,
    cancel_at_period_end: cancellationData.cancel_at_period_end,
    conversion_id: `cancellation_${cancellationData.subscription_id}_${Date.now()}`, // Unique ID for cancellation event
    ...serverData,
  })
}

/**
 * 客户端专用：结合客户端数据发送转化事件
 */
export async function trackClientSideConversion(
  eventType: ConversionEventType,
  eventData: Partial<ConversionEventData> = {},
): Promise<void> {
  const clientData = getClientTrackingData()

  await sendConversionEvent({
    event_type: eventType,
    ...eventData,
    ...clientData,
  })
}
